import { TrackedEvents } from '../../../features/tracking';
import { useTrack } from '../../../hooks/useTrack';
import { PhotoCaptureTimer } from './photoCaptureTimer';
import { type PhotoCaptureRequestStatus } from '../../../services/photoCapture';

export type PhotoCaptureCanceledBy = 'provider' | 'patient';

const updatePhotoCaptureTimer = (status: PhotoCaptureRequestStatus): void => {
  switch (status) {
    case 'requested': {
      PhotoCaptureTimer.start(status, Date.now());
      break;
    }
    case 'rejected':
    case 'uploaded':
    case 'error': {
      PhotoCaptureTimer.finish(status);
      break;
    }
    default: {
      PhotoCaptureTimer.step(status);
    }
  }
};

type PhotoCaptureEvents = {
  sendPhotoCaptureStartedEvent: () => void;
  sendPhotoCaptureTakenEvent: () => void;
  sendPhotoCaptureRetakenEvent: () => void;
  sendPhotoCaptureDownloadedEvent: () => void;
  sendPhotoCaptureCanceledEvent: (canceledBy: PhotoCaptureCanceledBy) => void;
  updatePhotoCaptureTimer: (status: PhotoCaptureRequestStatus) => void;
};

export const usePhotoCaptureEvents = (): PhotoCaptureEvents => {
  const track = useTrack();

  const sendPhotoCaptureStartedEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureStarted, {});
  };

  const sendPhotoCaptureTakenEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureTaken, {});
  };

  const sendPhotoCaptureRetakenEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureRetaken, {});
  };

  const sendPhotoCaptureDownloadedEvent = (): void => {
    void track(TrackedEvents.PhotoCaptureDownloaded, {});
  };

  const sendPhotoCaptureCanceledEvent = (
    canceledBy: PhotoCaptureCanceledBy,
  ): void => {
    void track(TrackedEvents.PhotoCaptureCanceled, {
      canceled_by: canceledBy,
    });
  };

  return {
    sendPhotoCaptureStartedEvent,
    sendPhotoCaptureTakenEvent,
    sendPhotoCaptureRetakenEvent,
    sendPhotoCaptureDownloadedEvent,
    sendPhotoCaptureCanceledEvent,
    updatePhotoCaptureTimer,
  };
};
